import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type Dispatch,
  type ReactNode,
} from 'react';
import type { ScanAction, ScanState, SignalStrength } from '@/types/health.types';

const initialState: ScanState = { status: 'idle', posture: null };

function scanReducer(state: ScanState, action: ScanAction): ScanState {
  switch (action.type) {
    case 'SET_POSTURE':
      if (state.status === 'idle') {
        return { ...state, posture: action.payload };
      }
      return state;
    case 'CALIBRATING':
      return { status: 'calibrating', signalStrength: action.payload };
    case 'FACE_NOT_DETECTED':
      return { status: 'face_not_detected', signalStrength: action.payload };
    case 'FACE_DETECTED':
      return {
        status: 'countdown',
        secondsRemaining: 3,
        signalStrength: action.payload,
      };
    case 'COUNTDOWN':
      return {
        status: 'countdown',
        secondsRemaining: action.payload.secondsRemaining,
        signalStrength: action.payload.signalStrength,
      };
    case 'PROGRESS':
      return {
        status: 'scanning',
        progress: action.payload.progress,
        signalStrength: action.payload.signalStrength,
      };
    case 'SIGNAL_STRENGTH': {
      const strength: SignalStrength = action.payload;
      if (state.status === 'calibrating' || state.status === 'face_not_detected') {
        return { ...state, signalStrength: strength };
      }
      if (state.status === 'countdown') {
        return { ...state, signalStrength: strength };
      }
      if (state.status === 'scanning') {
        return { ...state, signalStrength: strength };
      }
      return state;
    }
    case 'COMPLETE':
      return { status: 'complete', sessionId: action.payload };
    case 'ERROR':
      return {
        status: 'error',
        code: action.payload.code,
        message: action.payload.message,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

interface ScanContextValue {
  state: ScanState;
  dispatch: Dispatch<ScanAction>;
}

const ScanContext = createContext<ScanContextValue | null>(null);

export function ScanProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [state, dispatch] = useReducer(scanReducer, initialState);
  const value = useMemo(() => ({ state, dispatch }), [state]);
  return <ScanContext.Provider value={value}>{children}</ScanContext.Provider>;
}

export function useScan(): ScanContextValue {
  const ctx = useContext(ScanContext);
  if (!ctx) throw new Error('useScan must be used within ScanProvider');
  return ctx;
}
